import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { useCurrentUser } from '@/features/auth/state/auth'
import { Edit, Plus, Trash2, RefreshCw } from 'lucide-react'
import { useFetchProductHistory } from '@/features/products/services'

// Dados fictícios atualizados para incluir a ação
// const modificacoes = [
//   {
//     id: 1,
//     usuario: 'Maria Silva',
//     cpf: '123.456.789-00',
//     dataHora: '2023-10-08T14:30:00',
//     acao: 'editar',
//     campoModificado: 'Endereço',
//     valorAntigo: 'Rua das Flores, 123',
//     valorNovo: 'Avenida Principal, 456',
//     type: 'PREVIDENCIARIO'
//   },
//   {
//     id: 2,
//     usuario: 'João Santos',
//     cpf: '987.654.321-00',
//     dataHora: '2023-10-07T10:15:00',
//     acao: 'adicionar',
//     campoModificado: 'Telefone',
//     valorAntigo: '',
//     valorNovo: '(11) 91234-5678',
//     type: 'PREVIDENCIARIO'
//   },
//   {
//     id: 3,
//     usuario: 'Ana Oliveira',
//     cpf: '456.789.123-00',
//     dataHora: '2023-10-06T16:45:00',
//     acao: 'excluir',
//     campoModificado: 'E-mail',
//     valorAntigo: 'ana.oliveira@email.com',
//     valorNovo: '',
//     type: 'ICMS'
//   },
//   {
//     id: 4,
//     usuario: 'Carlos Mendes',
//     cpf: '789.123.456-00',
//     dataHora: '2023-10-05T09:20:00',
//     acao: 'atualizar',
//     campoModificado: 'Status',
//     valorAntigo: 'Pendente',
//     valorNovo: 'Aprovado',
//     type: 'Cliente'
//   }
// ]

// Função para obter o ícone baseado na ação
function getActionIcon(acao: string) {
  switch (acao) {
    case 'create':
    case 'adicionar':
      return <Plus className="h-5 w-5 text-green-500" />
    case 'delete':
    case 'excluir':
      return <Trash2 className="h-5 w-5 text-red-500" />
    case 'modify':
    case 'editar':
      return <Edit className="h-5 w-5 text-blue-500" />
    case 'update':
    case 'atualizar':
      return <RefreshCw className="h-5 w-5 text-yellow-500" />
    default:
      return null
  }
}

interface HistoricoProps {
  type: string
  productId?: string
}

export default function HistoricoModificacao({ type, productId }: HistoricoProps) {
  const user = useCurrentUser()
  const { data: modificacoes = [], isLoading } = useFetchProductHistory(productId)
  // Se user não existir ou não tiver as credenciais, retorna null
  if (user?.typeUser !== 'ADMIN' || user?.typeCargo !== 'CEO') return null

  const modificacoesFiltradas = Array.isArray(modificacoes)
    ? modificacoes.filter(mod => {
        if (productId) return true
        const summaryType = mod?.details?.summary?.typeServico
        return !summaryType || summaryType === type
      })
    : []

  const renderChanges = (details: Record<string, any> | undefined) => {
    if (!details) return []

    return Object.entries(details)
      .filter(([key]) => key !== 'summary')
      .map(([campo, value]) => ({
        campo,
        valorAntigo: value?.old ?? '—',
        valorNovo: value?.new ?? '—'
      }))
  }

  if (isLoading) {
    return (
      <Card className="w-full max-w-full bg-background p-1 mt-4 text-foreground">
        <CardHeader>
          <CardTitle className="text-xl text-foreground">Histórico de Modificações</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-foreground">Carregando histórico...</p>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className="w-full max-w-full bg-background p-1 mt-4 text-foreground">
      <CardHeader>
        <CardTitle className="text-xl text-foreground">Histórico de Modificações</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="max-h-[200px] pr-4 overflow-y-auto">
          {modificacoesFiltradas.length > 0 ? (
            modificacoesFiltradas.map((mod, index) => {
              const usuario = mod.actor?.name ?? 'Usuário não identificado'
              const cargo = [mod.actor?.typeCargo, mod.actor?.typeUser].filter(Boolean).join(' / ') || '—'
              const dataHora = mod.timestamp ? new Date(mod.timestamp).toLocaleString('pt-BR') : '—'
              const acao = String(mod.details?.summary?.acao || mod.action || '').toLowerCase()
              const alteracoes = renderChanges(mod.details)
              const campoPrincipal = alteracoes[0]?.campo ?? mod.details?.summary?.acao ?? mod.action ?? '—'

              return (
                <div key={mod.id ?? index} className="mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <div>
                      <h3 className="text-lg font-semibold">{usuario}</h3>
                      <p className="text-sm text-muted-foreground">{cargo}</p>
                    </div>
                    <p className="text-sm text-muted-foreground">{dataHora}</p>
                  </div>
                  <div className="p-3 rounded-md bg-background-secondary shadow-xl">
                    <div className="flex items-center gap-2 mb-2">
                      {getActionIcon(acao)}
                      <p className="font-medium capitalize">
                        {acao || 'ação'} - {campoPrincipal}
                      </p>
                    </div>
                    {alteracoes.length > 0 ? (
                      <div className="mt-2 space-y-3">
                        {alteracoes.map(item => (
                          <div key={`${mod.id}-${item.campo}`} className="grid grid-cols-1 md:grid-cols-3 gap-2">
                            <div>
                              <p className="text-sm font-medium">Campo</p>
                              <p className="text-sm break-all">{item.campo}</p>
                            </div>
                            <div>
                              <p className="text-sm font-medium">Valor antigo</p>
                              <p className="text-sm break-all">{String(item.valorAntigo)}</p>
                            </div>
                            <div>
                              <p className="text-sm font-medium">Valor novo</p>
                              <p className="text-sm break-all">{String(item.valorNovo)}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="mt-2">
                        <pre className="text-sm whitespace-pre-wrap break-all">
                          {JSON.stringify(mod.details?.summary ?? {}, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                  {index < modificacoesFiltradas.length - 1 && <Separator className="my-4" />}
                </div>
              )
            })
          ) : (
            <p className="text-sm text-foreground">Nenhuma modificação encontrada para este tipo.</p>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
